import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Award, Download, Printer, RefreshCw, Loader2, ShieldCheck, ExternalLink } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { Card, GoldButton, GhostButton } from "./UI";

const fetchPdf = async (no) => {
  const { data } = await api.get(`/certificates/${no}.pdf`, { responseType: "blob" });
  return URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }).toUpperCase();

function CertMeta({ cert }) {
  if (!cert) return <p className="text-xs text-slate-500" data-testid="cert-none">Δεν έχεις εκδώσει ακόμη πιστοποιητικό. Η «Λήψη PDF» εκδίδει το πρώτο με τις τρέχουσες κατατάξεις σου.</p>;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-mono text-slate-400" data-testid="cert-latest">
      <span className="text-[#E5B842]" data-testid="cert-latest-number">{cert.certificate_no}</span>
      <span>Έκδοση {fmtDate(cert.issued_at)}</span>
      <span data-testid="cert-latest-status" className={cert.status === "VALID" ? "text-emerald-300" : "text-rose-300"}>{cert.status}</span>
      <a href={`/verify/${cert.certificate_no}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#E5B842]" data-testid="cert-verify-link">
        <ExternalLink className="w-3 h-3" /> Επαλήθευση
      </a>
    </div>
  );
}

export default function CertificateSection() {
  const [img, setImg] = useState(null);
  const [latest, setLatest] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const printFrame = useRef(null);

  const load = useCallback(() => {
    Promise.all([
      api.get("/members/me/certificate/preview.png", { responseType: "blob" }),
      api.get("/members/me/certificates"),
    ]).then(([png, list]) => {
      setImg((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(png.data); });
      setLatest(list.data.certificates?.[0] || null);
      setError(null);
    }).catch(async (e) => {
      const blob = e?.response?.data;
      if (blob instanceof Blob) {
        try { e.response.data = JSON.parse(await blob.text()); } catch { /* not JSON */ }
      }
      setError(apiErr(e, "Το πιστοποιητικό δεν είναι διαθέσιμο"));
    });
  }, []);
  useEffect(load, [load]);

  const issue = async () => {
    const { data } = await api.post("/members/me/certificates");
    return data.certificate;
  };

  const run = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); } catch (e) { toast.error(apiErr(e, "Κάτι πήγε στραβά")); } finally { setBusy(null); }
  };

  const download = () => run("dl", async () => {
    const cert = latest && latest.status === "VALID" ? latest : await issue();
    const url = await fetchPdf(cert.certificate_no);
    const a = document.createElement("a");
    a.href = url; a.download = `WSTF-Certificate-${cert.certificate_no}.pdf`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    if (cert !== latest) load();
    toast.success(`Πιστοποιητικό ${cert.certificate_no}`);
  });

  const print = () => run("print", async () => {
    const cert = latest && latest.status === "VALID" ? latest : await issue();
    const frame = printFrame.current;
    frame.onload = () => { frame.contentWindow.focus(); frame.contentWindow.print(); };
    frame.src = await fetchPdf(cert.certificate_no);
    if (cert !== latest) load();
  });

  const reissue = () => run("new", async () => {
    const cert = await issue();
    toast.success(`Νέο πιστοποιητικό ${cert.certificate_no} με τις τρέχουσες κατατάξεις`);
    load();
  });

  return (
    <Card className="p-6" data-testid="settings-certificate">
      <div className="flex items-center gap-2 mb-1">
        <Award className="w-4 h-4 text-[#E5B842]" />
        <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-50">Certificate of Membership</h2>
      </div>
      <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-4">Πιστοποιητικό μέλους · A4 PDF με World & Country rank τη στιγμή της έκδοσης</p>
      {error ? (
        <p className="text-sm text-amber-300" data-testid="cert-error">{error}</p>
      ) : (
        <>
          <div className="rounded-lg overflow-hidden border border-[rgba(212,175,55,0.22)] bg-[#2a2926] aspect-[297/210]" data-testid="cert-preview">
            {img ? <img src={img} alt="Certificate preview" className="w-full h-full object-contain" data-testid="cert-preview-image" />
              : <div className="w-full h-full flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#E5B842]" /></div>}
          </div>
          <div className="mt-4"><CertMeta cert={latest} /></div>
          <div className="flex flex-wrap gap-2 mt-4">
            <GoldButton onClick={download} disabled={!!busy || !img} data-testid="cert-download-btn">
              {busy === "dl" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Λήψη PDF
            </GoldButton>
            <GhostButton onClick={print} disabled={!!busy || !img} data-testid="cert-print-btn">
              {busy === "print" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Εκτύπωση
            </GhostButton>
            {latest && (
              <GhostButton onClick={reissue} disabled={!!busy} data-testid="cert-reissue-btn">
                {busy === "new" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Νέο με τρέχουσες κατατάξεις
              </GhostButton>
            )}
          </div>
          <p className="mt-3 text-[11px] text-slate-500 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#E5B842]/70" /> Κάθε πιστοποιητικό έχει μοναδικό αριθμό και δημόσια σελίδα επαλήθευσης. Έως 5 νέες εκδόσεις ανά ημέρα.</p>
          <iframe ref={printFrame} title="print" className="hidden" data-testid="cert-print-frame" />
        </>
      )}
    </Card>
  );
}
